"use client";

import { AirdropFarmingBotDtoSchema, type AirdropFarmingBotDto, type AirdropFarmingBotStatus } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { ACTIVE_BOT_STATUSES, BOT_POLLING_INTERVAL_MS } from "@/constants/airdrop-farming";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

export function useAirdropFarmingBots(enabled = true) {
  const { hubUser, auth } = useHubUser();
  const apiHelper = useApi();

  const {
    data: bots = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<AirdropFarmingBotDto[]>({
    queryKey: [QueryKeys.AIRDROP_FARMING_BOTS, hubUser?.id],
    queryFn: async () => {
      const response = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_AIRDROP_FARMING_BOTS, {});
      return z.array(AirdropFarmingBotDtoSchema).parse(response);
    },
    enabled: enabled && !!hubUser?.id,
    staleTime: 10_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasActiveBots = data?.some((b) =>
        (ACTIVE_BOT_STATUSES as readonly AirdropFarmingBotStatus[]).includes(b.status),
      );
      // SSE handles real-time updates; polling is a fallback safety net
      return hasActiveBots ? BOT_POLLING_INTERVAL_MS : false;
    },
  });

  return {
    bots,
    isLoading: enabled && (isLoading || !hubUser?.id),
    isError,
    error,
    refetch,
  };
}
